// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Implement tape product.
 * @author James Reid
 */

// @ts-check

// @@imports-module
import { Product } from "./Product.js"

// @@imports-types
import {
    ProductData,
    PATCH_TYPES,
    TAPE_TYPES,
    UNITS,
    PRODUCT_DISPLAY_CATEGORIES
} from "../types/index.js"

// @@body
class Tape extends Product {
    /**
     * Construct tape product from data returned from server.
     * @param {ProductData} data Product data returned from server.
     * @param {any} match Regexp match data for internal calculator sku code
     * pertaining to this extended class.
     */
    constructor(data, match) {
        super(data, { unit: UNITS.length.metre })

        // Parse regexp data.
        const { type, shape, width, length } =
            /** @type {Object<string, string>} */ (match.groups)

        // Set extended class values.
        this.type = type === "S" ? TAPE_TYPES.seam
            : type === "M" ? TAPE_TYPES.mechanical
            : type === "C" ? TAPE_TYPES.cured
            : TAPE_TYPES.uncured
        this.displayCategory = PRODUCT_DISPLAY_CATEGORIES.tape
        this.patchType = shape === "E" ? PATCH_TYPES.external
            : shape === "I" ? PATCH_TYPES.internal
            : null

        // Infer exact dimensions required for calculations from calculator sku.
        this.width = parseInt(width) / 10000
        this.quantity = parseInt(length) / 100
    }
}

// @@exports
export { Tape }
